// @givmo/fundraisers-module — the ONE preview of a campaign email, shared by the designer (EMC-WEB10,
// mockups 14a–14d) and the email's own page (EMC-WEB1, mockups 15 and 17), so a person never sees two
// versions of the same email and wonders which one goes out (the polish-1 user test, 2026-10-31).
//
// Both read the same server render (`/emails/{id}/preview`, its first name the merge-field marker
// `PREVIEW_FIRST_NAME`), and both draw it here the same way: at the renderer's own width, scaled to the
// space it has, with the greeting's merge field as the "First name" chip and Givmo's footer marked
// "Added by Givmo · Always included". The designer adds its wash over what the assistant's last change
// added (`washAgainst`); nothing else differs.

import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { testIdProp } from '@givmo/design-system';
import { MessageEmailFrame } from './message-email-frame';
import { EMAIL_DESIGNER_COPY as S } from './email-designer-copy';
import { PREVIEW_FIRST_NAME } from './email-designer-transport';
import { washChanges } from './email-designer-wash';

/** The widths the preview draws the email at before scaling it: the renderer's desktop width with its
 *  margin, and a phone's. */
export const DESKTOP_WIDTH = 680;
export const PHONE_WIDTH = 390;
/** The email's own content width (the renderer's `CONTENT_WIDTH`). */
const EMAIL_CARD_WIDTH = 600;
/** How much of the preview's width the email's card takes (14b–14d: a 600px email drawn at about 0.8,
 *  its words readable, with the preview's grey either side). */
const CARD_SHARE = 0.74;

const SPARKLE_SVG =
  '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#3b5bdb" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z"/></svg>';
const LOCK_SVG =
  '<svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="11" x="3" y="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>';

/**
 * Draw the greeting's merge field as the "First name" chip (14b–14d): the preview is rendered with
 * {@link PREVIEW_FIRST_NAME} as its sample name, and each place it landed becomes the chip. The chip
 * tucks the punctuation after it against its edge, as against a name (the polish-1 user test: "Hi [First name] ,").
 */
export function drawFirstNameChips(doc: Document): void {
  const walker = doc.createTreeWalker(doc.body, 4 /* NodeFilter.SHOW_TEXT */);
  const hits: Text[] = [];
  for (let node = walker.nextNode(); node !== null; node = walker.nextNode()) {
    if ((node as Text).data.includes(PREVIEW_FIRST_NAME)) hits.push(node as Text);
  }
  for (const node of hits) {
    const parts = node.data.split(PREVIEW_FIRST_NAME);
    const fragment = doc.createDocumentFragment();
    parts.forEach((part, index) => {
      if (index > 0) {
        const chip = doc.createElement('span');
        chip.setAttribute('data-designer-merge-field', 'first_name');
        chip.style.cssText =
          'display:inline-block;padding:0 4px;margin:0 -1px 0 0;border-radius:4px;background:#e7edff;color:#3b5bdb;font-size:0.82em;font-weight:600;line-height:1.55;vertical-align:baseline;';
        chip.textContent = S.firstNameChip;
        fragment.append(chip);
      }
      if (part.length > 0) fragment.append(doc.createTextNode(part));
    });
    node.replaceWith(fragment);
  }
}

/** The footer cell: the renderer's last block row, the one holding its Unsubscribe link. */
function footerCell(doc: Document): HTMLElement | null {
  const cells = Array.from(doc.querySelectorAll<HTMLElement>('td.emc-px'));
  return (
    cells
      .reverse()
      .find((cell) =>
        Array.from(cell.querySelectorAll('a')).some((link) =>
          /unsubscribe/i.test(link.textContent ?? ''),
        ),
      ) ?? null
  );
}

/**
 * Mark Givmo's footer ("Added by Givmo · Always included"), and draw the empty email's placeholder.
 *
 * Every style here is written through CSSOM (`style.cssText`), never as a `style` attribute: the frame's
 * document inherits the consoles' `style-src 'self'` (`message-email-frame.tsx`), which refuses a style
 * attribute set from script, so the placeholder drew as bare serif text outside the email's look.
 */
function decorateEmail(doc: Document, options: { empty: boolean; orgName: string }): void {
  const footer = footerCell(doc);
  if (footer === null) return;
  footer.style.position = 'relative';
  const chip = doc.createElement('div');
  chip.setAttribute('data-designer-footer-chip', '');
  chip.style.cssText =
    'position:absolute;left:50%;top:9px;transform:translateX(-50%);display:inline-flex;align-items:center;gap:4px;padding:2px 9px;border:1px solid #e3e6eb;border-radius:999px;background:#ffffff;font:500 11px/16px Lato,Helvetica,Arial,sans-serif;color:#6b7280;white-space:nowrap;';
  chip.innerHTML = LOCK_SVG;
  chip.append(doc.createTextNode(S.footerChip));
  footer.append(chip);
  if (!options.empty) return;
  const row = footer.closest('tr');
  if (row === null) return;
  const placeholder = doc.createElement('tr');
  placeholder.setAttribute('data-designer-placeholder', '');
  const cell = doc.createElement('td');
  cell.style.cssText = 'padding:20px 20px 8px 20px;';
  const box = doc.createElement('div');
  box.style.cssText =
    'border:1px dashed #d7dbe2;border-radius:6px;padding:110px 32px;text-align:center;font-family:Lato,Helvetica,Arial,sans-serif;';
  const icon = doc.createElement('div');
  icon.style.cssText =
    'width:36px;height:36px;margin:0 auto 12px auto;border-radius:999px;background:#eef2ff;display:flex;align-items:center;justify-content:center;';
  icon.innerHTML = SPARKLE_SVG;
  const title = doc.createElement('div');
  title.style.cssText = 'font-size:15px;font-weight:700;color:#1f2937;margin-bottom:10px;';
  title.textContent = S.emailEmptyTitle;
  const body = doc.createElement('div');
  body.style.cssText =
    'font-size:12.5px;line-height:1.6;color:#374151;max-width:260px;margin:0 auto;';
  body.textContent = S.emailEmptyBody(options.orgName);
  box.append(icon, title, body);
  cell.append(box);
  placeholder.append(cell);
  row.before(placeholder);
}

/**
 * Where the stage scrolls to bring the assistant's changes into view, or null to stay put. All of them
 * in view when they fit, else the first at the top. The aim always lands on a block's own top (the
 * email's top included), never inside one, so the logo, the accent bar or a line of text is never cut
 * in half (the polish-1 user test, 14c: a sliver of the tagline above the hero). Between the two block
 * tops either side of the aim, the lower one wins while it keeps the first change in view.
 */
export function changeScrollTop(input: {
  /** The changes' extent, in the stage's own pixels. */
  first: number;
  last: number;
  /** Every block's top, in the stage's own pixels. */
  blockTops: number[];
  view: number;
  current: number;
}): number | null {
  const { first, last, view, current } = input;
  let aim = current;
  if (last - first > view || first < current) aim = first;
  else if (last > current + view) aim = Math.min(first, last - view);
  if (aim === current) return null;
  const tops = [0, ...input.blockTops].sort((a, b) => a - b);
  const up = Math.max(...tops.filter((top) => top <= aim));
  const down = tops.find((top) => top >= aim);
  const top = down !== undefined && down <= first ? down : up;
  const next = Math.max(0, top);
  return next === current ? null : next;
}

export function EmailPreviewStage({
  html,
  subject,
  width,
  empty,
  orgName,
  washAgainst = null,
  testIdPrefix,
  title,
  overlay,
}: {
  html: string;
  subject: string;
  width: 'desktop' | 'phone';
  /** An email with no blocks yet draws the designer's placeholder inside it (14a). */
  empty: boolean;
  orgName: string;
  /** The render before the assistant's last change: what's new since is washed (14c, 14d). */
  washAgainst?: string | null;
  /** `email-designer` in the designer, `fundraiser-email` on the email's page. */
  testIdPrefix: string;
  /** The frame's name for a screen reader. */
  title: string;
  /** Drawn over the email, in the email's own place and scale (the designer's hand editor): the frame's
   *  drawn document, a count that moves on every draw, the scale and what holds them. */
  overlay?: (frame: {
    doc: Document | null;
    drawn: number;
    scale: number;
    container: HTMLElement | null;
  }) => ReactNode;
}) {
  const frameDoc = useRef<Document | null>(null);
  const [drawnDoc, setDrawnDoc] = useState<{ doc: Document | null; drawn: number }>({
    doc: null,
    drawn: 0,
  });
  const decorate = useCallback(
    (doc: Document) => {
      // Wash before decorating: the footer's chip and the merge-field chip are the preview's, never a
      // change of the email's (the merge field's marker is the same word before and after a change).
      if (washAgainst !== null) washChanges(doc, washAgainst);
      drawFirstNameChips(doc);
      decorateEmail(doc, { empty, orgName });
      frameDoc.current = doc;
      setDrawnDoc((current) => ({ doc, drawn: current.drawn + 1 }));
    },
    [washAgainst, empty, orgName],
  );
  const natural = width === 'phone' ? PHONE_WIDTH : DESKTOP_WIDTH;
  const stage = useRef<HTMLDivElement>(null);
  const sheet = useRef<HTMLDivElement>(null);
  const [room, setRoom] = useState<{ width: number; height: number } | null>(null);
  const [frameHeight, setFrameHeight] = useState(0);
  useEffect(() => {
    const node = stage.current;
    if (node === null) return;
    const measure = () => setRoom({ width: node.clientWidth, height: node.clientHeight });
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);
  // A new width starts at the email's top: its logo and accent bar (the phone preview kept the desktop
  // one's scroll, so it opened partway down, its head out of view).
  useEffect(() => {
    stage.current?.scrollTo?.({ top: 0 });
  }, [width]);
  const scale =
    room === null || room.width <= 0
      ? 1
      : Math.min(1, room.width / natural, (room.width * CARD_SHARE) / EMAIL_CARD_WIDTH);
  // After a turn, bring the first change into view: the wash and the new block's outline are the point.
  // The email's height settles as its images load, so the aim is kept for a moment and re-taken.
  const aimAt = useRef<string | null>(null);
  useEffect(() => {
    if (washAgainst === null) return;
    aimAt.current = washAgainst;
    const settle = setTimeout(() => {
      if (aimAt.current === washAgainst) aimAt.current = null;
    }, 2500);
    return () => clearTimeout(settle);
  }, [washAgainst]);
  useEffect(() => {
    if (aimAt.current === null || aimAt.current !== washAgainst) return;
    const box = stage.current;
    const doc = frameDoc.current;
    const marks = Array.from(doc?.querySelectorAll('[data-designer-added]') ?? []);
    if (box === null || doc === null || sheet.current === null || marks.length === 0) return;
    // Each change is aimed at by the block it sits in, and the aim lands on a block's top.
    const blocks = marks.map((mark) => mark.closest('td.emc-px') ?? mark);
    const rects = blocks.map((block) => block.getBoundingClientRect());
    const offset = sheet.current.offsetTop;
    const next = changeScrollTop({
      first: offset + Math.min(...rects.map((r) => r.top)) * scale,
      last: offset + Math.max(...rects.map((r) => r.bottom)) * scale,
      blockTops: Array.from(
        doc.querySelectorAll('td.emc-px'),
        (cell) => offset + cell.getBoundingClientRect().top * scale,
      ),
      view: box.clientHeight,
      current: box.scrollTop,
    });
    if (next !== null) box.scrollTo?.({ top: next, behavior: 'smooth' });
  }, [frameHeight, scale, washAgainst]);
  // A fade at the foot while more of the email is below, so its fold never reads as a cut-off button
  // (the polish-1 user test, 14b's "Give today").
  const [moreBelow, setMoreBelow] = useState(false);
  useEffect(() => {
    const node = stage.current;
    if (node === null) return;
    const update = () => setMoreBelow(node.scrollHeight - node.clientHeight - node.scrollTop > 4);
    update();
    node.addEventListener('scroll', update, { passive: true });
    return () => node.removeEventListener('scroll', update);
  }, [frameHeight, scale, room]);

  return (
    <div className="relative flex min-h-0 flex-1 flex-col">
      <div
        ref={stage}
        className="relative min-h-0 flex-1 overflow-y-auto"
        {...testIdProp(`${testIdPrefix}-stage-scroll`)}
      >
        <div
          ref={sheet}
          className="relative mx-auto"
          style={{
            width: natural * scale,
            height: frameHeight > 0 ? frameHeight * scale : undefined,
          }}
          {...testIdProp(`${testIdPrefix}-stage`)}
          data-scale={scale.toFixed(3)}
        >
          <div
            style={{
              width: natural,
              transform: `scale(${scale})`,
              transformOrigin: 'top left',
            }}
          >
            <MessageEmailFrame
              key={width}
              html={html}
              title={subject.trim() === '' ? title : subject}
              decorate={decorate}
              onHeight={setFrameHeight}
              testId={`${testIdPrefix}-frame`}
              className="block w-full bg-transparent"
            />
          </div>
          {overlay?.({
            doc: drawnDoc.doc,
            drawn: drawnDoc.drawn,
            scale,
            container: sheet.current,
          })}
        </div>
      </div>
      {moreBelow && (
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-neutral-50/60 to-transparent"
        />
      )}
    </div>
  );
}
